import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import { useAuth } from "contexts/AuthContext";
import { StatCard, TwoLineListSection } from "components/dashboard";
import { DashboardLayout } from "components/layouts";
import { EPath } from "constants/path";

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Admin-specific stats data
  const statsData = [
    {
      title: "Total Users",
      value: 1250,
      change: 12.5,
      changeText: "Since last month",
      actionText: "View All Users",
    },
    {
      title: "Active Practices",
      value: 45,
      change: 8.2,
      changeText: "Since last month",
      actionText: "View Practices",
    },
    {
      title: "System Revenue",
      value: "$125K",
      change: 15.3,
      changeText: "Since last month",
      actionText: "View Revenue",
    },
    {
      title: "Support Tickets",
      value: 23,
      change: -5.7,
      changeText: "Since last week",
      actionText: "View Tickets",
    },
  ];

  // Recent activities
  const recentActivities = [
    {
      subtitle: "New Practice Registration",
      content: "MedCare Clinic registered and completed onboarding",
    },
    {
      subtitle: "User Account Created",
      content: "Dr. Sarah Johnson created account for HeartCare Practice",
    },
    {
      subtitle: "Payment Processed",
      content: "Premium subscription payment received from Family Health",
    },
    {
      subtitle: "Support Ticket Resolved",
      content: "Issue #1234 - Login problems resolved for Downtown Medical",
    },
    {
      subtitle: "System Update",
      content: "Version 2.1.3 deployed successfully to production",
    },
  ];

  // System alerts
  const systemAlerts = [
    {
      subtitle: "High Server Load",
      content: "Server CPU usage at 85% - Monitor closely",
    },
    {
      subtitle: "Database Backup",
      content: "Scheduled backup completed successfully",
    },
    {
      subtitle: "SSL Certificate",
      content: "SSL certificate expires in 30 days",
    },
    {
      subtitle: "New Feature Request",
      content: "Bulk user import feature requested by 5 practices",
    },
  ];

  return (
    <DashboardLayout title="Dashboard">
      <Box>
        {/* Welcome Header */}
        <Box className="mb-8">
          <Typography variant="body1" className="text-gray-600">
            Welcome back, {user?.email}. Here's what's happening with your
            system.
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: "10px",
            marginBottom: "8px",
          }}
        >
          {statsData.map((stat) => (
            <StatCard
              key={stat.title}
              title={stat.title}
              value={stat.value}
              change={stat.change}
              changeText={stat.changeText}
              actionText={stat.actionText}
              onActionClick={() => console.log(`${stat.title} clicked`)}
            />
          ))}
        </Box>

        {/* Content Sections */}
        <Box
          sx={{
            display: "flex",
            gap: "10px",
            marginBottom: "8px",
          }}
        >
          <TwoLineListSection
            title="Recent Activities"
            items={recentActivities}
            height="310px"
          />

          <TwoLineListSection
            title="System Alerts"
            items={systemAlerts}
            height="310px"
          />
        </Box>

        {/* Quick Actions */}
        <Box className="bg-white rounded-lg shadow p-6">
          <Typography variant="h6" className="mb-4">
            Quick Actions
          </Typography>
          <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Box
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              onClick={() => navigate(EPath.INVITE_ADMIN_USER)}
            >
              <Typography variant="subtitle2" className="font-semibold mb-2">
                Invite Admin User
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                Invite a new admin user to the portal
              </Typography>
            </Box>
            <Box
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              onClick={() => navigate(EPath.PATIENT_MANAGEMENT)}
            >
              <Typography variant="subtitle2" className="font-semibold mb-2">
                Patient Management
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                View and manage patient access across practices
              </Typography>
            </Box>
            <Box className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              <Typography variant="subtitle2" className="font-semibold mb-2">
                System Analytics
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                View detailed system performance metrics
              </Typography>
            </Box>
            <Box className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              <Typography variant="subtitle2" className="font-semibold mb-2">
                Support Center
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                Manage support tickets and user requests
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default HomePage;
